import { useMemo } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import type { ReportRange, StockFlowReport } from '@eombor/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import type { TrendPoint } from '../model/chart-data.js';
import { ReportCard } from './ReportCard.js';
import { TrendChart } from './TrendChart.js';

/**
 * Nuqtalar soni ko'p bo'lsa yorliq FAQAT ba'zi nuqtalarda chiziladi.
 *
 * 90 kunlik davrda har kun tagiga sana yozilsa, matn bir-birining ustiga
 * chiqib o'qilmay qoladi. Nuqtaning O'ZI qoladi — chizilmaydigan faqat
 * yozuv, ya'ni grafikning shakli to'liq ko'rinaveradi.
 */
const MAX_LABELS = 12;

/** `2026-09-03` → `03.09` — o'q ostida yil ortiqcha. */
const shortDate = (iso: string): string => {
  const parts = iso.split('-');
  return `${parts[2] ?? ''}.${parts[1] ?? ''}`;
};

export interface StockFlowBlockProps {
  query: UseQueryResult<StockFlowReport, ApiClientError>;
  range: ReportRange;
}

export const StockFlowBlock = ({ query, range }: StockFlowBlockProps) => {
  const points = useMemo<TrendPoint[]>(() => {
    const series = query.data?.series ?? [];
    const step = Math.max(1, Math.ceil(series.length / MAX_LABELS));
    return series.map((point, index) => ({
      label: index % step === 0 ? shortDate(point.date) : '',
      receipts: point.inQty,
      issues: point.outQty,
    }));
  }, [query.data]);

  /* "Bo'sh" degani — davrda birorta harakat bo'lmagan. Qator O'ZI bo'sh
     emas (har kun uchun nuqta bor), shuning uchun uzunligiga emas,
     JAMIGA qaraladi: nol chizig'i "ma'lumot yo'q" deb o'qilardi. */
  const totals = query.data?.totals;
  const empty = totals !== undefined && totals.inQty === 0 && totals.outQty === 0;

  return (
    <ReportCard
      icon={ArrowLeftRight}
      tone="accent"
      title={t('report.flowTitle')}
      hint={t('report.flowHint', { from: range.from, to: range.to })}
      pending={query.isPending}
      error={query.error ?? null}
      empty={empty}
      emptyText={t('report.flowEmpty')}
    >
      <TrendChart
        points={points}
        receiptsLabel={t('report.flowIn')}
        issuesLabel={t('report.flowOut')}
        caption={t('report.flowTitle')}
      />
    </ReportCard>
  );
};
